import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceResource } from "../../common/resource";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

// The resources found by their uuid, for the values that only store uuids (the resource secondary criteria),
// without loading the whole planzone list. They are kept until the planzone changes.
const initialState: State = {
  resources: {},
  pendingUuids: [],
  missingUuids: [],
};

function handleRequest(
  state: State,
  { uuids }: Boundaries["retrieveResourcesByUuids"]["request"],
): State {
  return {
    ...state,
    pendingUuids: [
      ...state.pendingUuids,
      ...uuids.filter((uuid) => !state.pendingUuids.includes(uuid)),
    ],
  };
}

function handleSuccess(
  state: State,
  { uuids, resources }: Boundaries["retrieveResourcesByUuids"]["success"],
): State {
  const found = resources.reduce(
    (map, resource) => ({ ...map, [resource.uuid]: resource }),
    state.resources,
  );

  return {
    resources: found,
    pendingUuids: state.pendingUuids.filter((uuid) => !uuids.includes(uuid)),
    missingUuids: [
      ...state.missingUuids,
      ...uuids.filter(
        (uuid) => !found[uuid] && !state.missingUuids.includes(uuid),
      ),
    ],
  };
}

function handleFailure(
  state: State,
  { uuids }: Boundaries["retrieveResourcesByUuids"]["failure"],
): State {
  return {
    ...state,
    pendingUuids: state.pendingUuids.filter((uuid) => !uuids.includes(uuid)),
    missingUuids: [
      ...state.missingUuids,
      ...uuids.filter((uuid) => !state.missingUuids.includes(uuid)),
    ],
  };
}

// Edited names, avatars or tags follow.
function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    resources: Object.fromEntries(
      Object.entries(state.resources).map(([uuid, resource]) => [
        uuid,
        reduceResource(resource, event),
      ]),
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveResourcesByUuidsRequested]: handleRequest,
      [events.retrieveResourcesByUuidsSucceeded]: handleSuccess,
      [events.retrieveResourcesByUuidsFailed]: handleFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
